import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, Youtube, Instagram, ExternalLink, Users } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { ShowreelProvider } from "@/components/site/Showreel";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { MediaView, mediaWrapClass } from "@/components/site/MediaView";
import { FramedImage } from "@/components/site/FramedImage";
import { SmartGallery } from "@/components/site/SmartGallery";
import { MapsWaze } from "@/components/site/MapsWaze";
import { JoinDialog } from "@/components/site/JoinDialog";
import { isLocked, isPast, pastBadgeLabel } from "@/lib/publish";
import { NewsActionBlock } from "@/components/site/NewsActionBlock";
import { ActionButtons } from "@/components/site/ActionButtons";
import { galleryCover } from "@/lib/media";

export const newsSessionRemaining = (s) => {
  const cap = Number(s.capacity);
  if (!cap || cap <= 0) return null; // null = unlimited / no counter
  return Math.max(0, cap - Number(s.taken || 0));
};
const sessionLabel = (s) => [s.date, [s.startTime, s.endTime].filter(Boolean).join("–"), s.locationName].filter(Boolean).join(" · ");

const NewsBody = () => {
  const { content } = useContent();
  const { id } = useParams();
  const items = content.news?.items || [];
  const item = items.find((it) => it.id === id && (it.status || "published") !== "hidden");
  const related = items.filter((it) => it.id !== id && !["hidden", "archived"].includes(it.status || "published")).slice(0, 3);

  const [reg, setReg] = useState(null);   // session object for registration
  const [join, setJoin] = useState(null); // role label for "rejoindre l'aventure"
  const locked = item ? isLocked(item) : false; // "Bientôt disponible" → actions verrouillées
  const past = item ? isPast(item) : false; // archivé → actions fermées
  const closedAction = locked || past;

  // Fix duplicate text: only show excerpt as lead if it isn't already inside the content
  const showExcerpt = item?.excerpt && (!item.content || !item.content.includes(item.excerpt.trim()));
  const openRoles = (item?.joinRoles || []).filter((r) => !r.closed).map((r) => r.label).filter(Boolean);
  const socials = item ? [
    item.youtube && { href: item.youtube, icon: Youtube, label: "Voir sur YouTube" },
    item.instagram && { href: item.instagram, icon: Instagram, label: "Voir sur Instagram" },
    item.externalUrl && { href: item.externalUrl, icon: ExternalLink, label: "Découvrir la vidéo" },
  ].filter(Boolean) : [];

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24">
        {!item ? (
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
            <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-4">Erreur 404</div>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Actualité introuvable</h1>
            <Link to="/#news" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><ArrowLeft size={15} /> Toutes les actualités</Link>
          </div>
        ) : (
          <article className="max-w-5xl mx-auto px-6 md:px-12" data-testid="news-detail">
            <Reveal>
              <Link to="/#news" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Retour aux actualités</Link>
              <div className="flex items-center gap-4 mb-5">
                <span className="text-[11px] tracking-[0.3em] uppercase text-electric">{item.category || "Actualité"}</span>
                {item.date && <span className="text-[11px] tracking-[0.2em] uppercase text-secondary2">{item.date}</span>}
                {past && <span className="text-[10px] tracking-[0.2em] uppercase text-purple-200 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-400/40" data-testid="news-detail-past-badge">{pastBadgeLabel(item)}</span>}
              </div>
              <h1 data-testid="news-detail-title" className="font-serif text-4xl md:text-6xl text-white tracking-tight leading-[0.98] mb-6">{item.title}</h1>
              {(item.locationName || item.address) && (
                <div className="mb-8"><MapsWaze name={item.locationName} address={item.address} testid="news-detail-location" /></div>
              )}
            </Reveal>

            {(item.image || item.videoUrl || item.mediaType === "slideshow") && (
              <Reveal className="mb-12">
                <div className={mediaWrapClass(item.detailFrame).cls}>
                  <MediaView item={item} alt={item.title} frame={item.detailFrame} />
                </div>
              </Reveal>
            )}

            {(showExcerpt || item.content) && (
              <Reveal className="mb-12">
                {showExcerpt && <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed mb-6">{item.excerpt}</p>}
                {item.content && <div className="text-base md:text-lg text-secondary2 font-light leading-relaxed whitespace-pre-line">{item.content}</div>}
              </Reveal>
            )}

            {socials.length > 0 && (
              <Reveal className="mb-12 flex flex-wrap gap-3" data-testid="news-social-links">
                {socials.map((s, x) => (
                  <a key={x} href={s.href} target="_blank" rel="noreferrer" data-testid={`news-social-${x}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-[12px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors">
                    <s.icon size={16} /> {s.label}
                  </a>
                ))}
              </Reveal>
            )}

            {(item.gallery || []).length > 0 && (
              <Reveal className="mb-14"><SmartGallery images={item.gallery} options={item.gallerySettings || { mode: item.galleryMode || "fade", autoplay: item.galleryAutoplay !== false }} label="Galerie photo" /></Reveal>
            )}

            {/* Sessions + registration */}
            {item.registrationEnabled && (item.sessions || []).length > 0 && (
              <Reveal className="mb-14" data-testid="news-sessions">
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-6">Sessions & inscription</h2>
                <div className="space-y-4">
                  {item.sessions.map((s, si) => {
                    const rem = newsSessionRemaining(s);
                    const full = rem === 0;
                    return (
                      <div key={s.id || si} data-testid={`news-session-${si}`} className="rounded-xl border border-white/10 bg-surface/40 p-5 flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                            {s.date && <span className="inline-flex items-center gap-2 text-white"><CalendarDays size={15} className="text-electric" /> {s.endDate && s.endDate !== s.date ? `${s.date} → ${s.endDate}` : s.date}</span>}
                            {s.allDay
                              ? <span className="inline-flex items-center gap-2 text-white/85 text-sm"><Clock size={14} className="text-electric" /> Toute la journée</span>
                              : (s.startTime || s.endTime) && <span className="inline-flex items-center gap-2 text-white/85 text-sm"><Clock size={14} className="text-electric" /> {[s.startTime, s.endTime].filter(Boolean).join(" – ")}</span>}
                          </div>
                          {(s.locationName || s.address) && <MapsWaze name={s.locationName} address={s.address} testid={`news-session-map-${si}`} />}
                          {rem != null && <div className={`text-xs ${full ? "text-red-400" : "text-electric"}`}><Users size={12} className="inline mr-1" />{full ? "COMPLET" : `${rem} places restantes`}</div>}
                        </div>
                        <button data-testid={`news-session-register-${si}`} disabled={full || closedAction} onClick={() => setReg(s)}
                          className={`shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors ${full || closedAction ? "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed" : "bg-electric text-black hover:bg-white"}`}>
                          {past ? "Terminé" : locked ? "Bientôt disponible" : full ? "Complet" : "S'inscrire"} {!full && !closedAction && <ArrowRight size={14} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            )}

            {/* Rejoindre l'aventure (dynamic roles) */}
            {item.joinEnabled && openRoles.length > 0 && (
              <Reveal className="rounded-2xl border border-electric/25 bg-gradient-to-br from-surface/70 to-[#050505] p-8 md:p-10" data-testid="news-join-block">
                <h2 className="font-serif text-3xl text-white mb-3">Rejoindre l'aventure</h2>
                <p className="text-secondary2 font-light mb-7">Ce projet recrute — choisissez le rôle qui vous correspond.</p>
                <div className="flex flex-wrap gap-3">
                  {(item.joinRoles || []).map((r, ri) => {
                    const closed = r.closed;
                    return (
                      <button key={ri} data-testid={`news-join-role-${ri}`} disabled={closed || closedAction} onClick={() => setJoin(r.label)}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-[12px] tracking-[0.1em] uppercase font-semibold transition-colors ${closed || closedAction ? "bg-white/5 text-white/35 border border-white/10 cursor-not-allowed" : "bg-electric text-black hover:bg-white"} ${closed ? "line-through" : ""}`}>
                        {r.label}{r.needed ? ` (${r.needed})` : ""} {closed && "— Complet"}
                      </button>
                    );
                  })}
                </div>
              </Reveal>
            )}

            {(item.actions || []).length > 0 && (
              <Reveal className="mt-10"><ActionButtons actions={item.actions} context={{ refId: item.id, refTitle: item.title }} /></Reveal>
            )}

            <Reveal><NewsActionBlock item={item} content={content} /></Reveal>

            {related.length > 0 && (
              <div className="mt-24 border-t border-white/10 pt-12">
                <h2 className="font-serif text-3xl text-white mb-8">À lire aussi</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {related.map((it, i) => {
                    const cover = it.mediaType === "slideshow" ? galleryCover(it.gallery) : it.image;
                    return (
                      <Link key={it.id || i} to={`/actualites/${it.id}`} data-testid={`related-news-${i}`} className="group rounded-lg overflow-hidden border border-white/10 bg-surface/60 hover:border-electric/40 transition-colors">
                        <div className="h-40 overflow-hidden bg-[#0e0e0e]">{cover && <FramedImage src={cover} frame={it.imageFrame} alt={it.title} className="group-hover:scale-105 transition-transform duration-700" />}</div>
                        <div className="p-5"><div className="text-[10px] tracking-[0.2em] uppercase text-electric mb-2">{it.category}</div><h3 className="font-serif text-lg text-white">{it.title}</h3></div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </article>
        )}
      </main>
      <Footer />
      {item && <JoinDialog open={!!reg} onClose={() => setReg(null)} type="inscription" refId={item.id} refTitle={item.title}
        sessionId={reg?.id} sessionLabel={reg ? sessionLabel(reg) : ""} questions={item.questions || []} roles={[]} heading={`S'inscrire — ${item.title}`} intro={reg ? sessionLabel(reg) : ""} />}
      {item && <JoinDialog open={!!join} onClose={() => setJoin(null)} type="casting" refId={item.id} refTitle={item.title}
        roles={openRoles} initialRole={join || ""} questions={item.questions || []} heading="Rejoindre l'aventure" intro={`Projet : ${item.title}`} />}
    </div>
  );
};

export default function NewsDetail() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <NewsBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
